import { useAuth } from '@/zustand/auth-store';
import { useUserType } from '@/zustand/user-type-store';
import { USER_TYPES } from '@/constants/constants';
import {
  getUserTypeFromRole,
  isClinicOwnerRole,
  isSoloTherapistRole,
  isTherapistRole,
  setUserTypeEverywhere,
} from '@/utils/detect-user-type';
import { getHomePath } from '@/utils/helpers';

/**
 * Multi-account sign in (API_CONTRACT_AUTH_ACCOUNTS.md): one person (one phone) can hold
 * several accounts - a therapist in a clinic who also runs a solo practice, a staff member
 * who is also a patient, a super admin who is also a clinic member. A general sign-in link
 * then answers with { status: 'ACCOUNT_SELECTION_REQUIRED', selectionToken, accounts }
 * instead of tokens, and the person picks one on /select-account.
 *
 * Account item: { membershipId, clinicId, clinicName, clinicLogoUrl, kind, role, status }.
 */
export const ACCOUNT_SELECTION_REQUIRED = 'ACCOUNT_SELECTION_REQUIRED';

export const ACCOUNT_KIND = {
  STAFF: 'STAFF',
  PATIENT: 'PATIENT',
  PLATFORM: 'PLATFORM',
};

export const ACCOUNT_STATUS = {
  INVITED: 'INVITED',
  ACTIVE: 'ACTIVE',
  INVITATION_NOT_DELIVERED: 'INVITATION_NOT_DELIVERED',
};

// The one stable error code the backend sends for an ended (deactivated / discharged) account.
export const ACCOUNT_DEACTIVATED = 'ACCOUNT_DEACTIVATED';

export const isAccountDeactivatedError = error =>
  error?.response?.data?.error === ACCOUNT_DEACTIVATED;

const STORAGE_KEY = 'rehab-account-selection';

/** The pending account choice in a magic-link response, or null for a direct sign in. */
export const getAccountSelection = (data, phone = null) => {
  if (data?.status !== ACCOUNT_SELECTION_REQUIRED || !Array.isArray(data.accounts)) {
    return null;
  }
  return {
    selectionToken: data.selectionToken,
    phone,
    accounts: data.accounts,
  };
};

// sessionStorage: the choice only lives as long as this sign-in tab.
export const saveAccountSelection = (selection, destination) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...selection, destination }));
  } catch (_) {
    // Storage blocked - /select-account will send the user back to sign in.
  }
};

export const readAccountSelection = () => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) {
    return null;
  }
};

export const clearAccountSelection = () => {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch (_) {
    // Nothing to clear.
  }
};

/**
 * `mid` claim of the access token - the signed-in account's membershipId (null for the
 * super admin's platform account). Decoded, not verified: only used to mark the current
 * account in the switcher.
 */
export const getTokenMembershipId = accessToken => {
  try {
    const payload = accessToken?.split('.')[1];
    if (!payload) {
      return null;
    }
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded))?.mid ?? null;
  } catch (_) {
    return null;
  }
};

/**
 * Stores the { token, data: user } of a magic-link / select-account / switch-account
 * response. Returns the resolved user type.
 */
export const applySignInResponse = data => {
  const token = data?.token;
  const user = data?.data;
  const { setAccessToken, setRefreshToken, setUser } = useAuth.getState();

  if (token?.accessToken) {
    setAccessToken(token.accessToken);
    setRefreshToken(token.refreshToken ?? '');
  }
  if (user) {
    localStorage.setItem('user-info', JSON.stringify(user));
    setUser(user);
  }

  const resolvedUserType = getUserTypeFromRole(user?.role);
  setUserTypeEverywhere(useUserType.getState().setUserType, resolvedUserType);
  return resolvedUserType;
};

const APP_HOME_PATH = {
  [USER_TYPES.PATIENT]: '/patients/home',
  [USER_TYPES.DASHBOARD]: '/dashboard/physiotherapists',
};

/**
 * Where a signed-in account goes (magic link, select-account, switch-account), from the
 * answer's `data.onboardingCompleted` (contract §4.7): true opens the app directly, false
 * opens that account's onboarding. `redirect` (a link's in-app path) wins once onboarding
 * is done; a patient keeps it through onboarding.
 */
export const getSignedInDestination = (user, redirect = null) => {
  const userType = getUserTypeFromRole(user?.role);
  const safeRedirect = typeof redirect === 'string' && redirect.startsWith('/') ? redirect : null;
  const patientOnboardingPath = safeRedirect
    ? `/patients/onboarding?redirect=${encodeURIComponent(safeRedirect)}`
    : '/patients/onboarding';

  // An answer without the flag: the onboarding gates read /onboarding/status themselves.
  if (typeof user?.onboardingCompleted !== 'boolean') {
    if (userType === USER_TYPES.PATIENT) {
      return patientOnboardingPath;
    }
    return safeRedirect || getHomePath();
  }

  if (!user.onboardingCompleted) {
    if (userType === USER_TYPES.PATIENT) {
      return patientOnboardingPath;
    }
    if (isClinicOwnerRole(user.role)) {
      return '/clinic-setup';
    }
    if (isTherapistRole(user.role)) {
      return '/therapist-onboarded';
    }
  }

  return safeRedirect || APP_HOME_PATH[userType] || '/overview';
};

export const isSoloAccount = account => isSoloTherapistRole(account?.role);

export const isPlatformAccount = account => account?.kind === ACCOUNT_KIND.PLATFORM;

/**
 * Display name of an account: the clinic's name, or a fixed label - a solo workspace has
 * no name (clinicName: ''), and the platform account has no clinic.
 */
export const getAccountName = (account, t) => {
  if (isPlatformAccount(account)) {
    return t('accounts.platform_account', { ns: 'common' });
  }
  if (isSoloAccount(account)) {
    return t('accounts.independent_therapist', { ns: 'common' });
  }
  return account?.clinicName || t('accounts.clinic_account', { ns: 'common' });
};
